import { Input, Output, EventEmitter, Component, OnInit, ViewChild } from '@angular/core';
import { PurchasingUnitsComponent } from './purchasingUnits/purchasingUnits.component';

import { ViewService } from '../../../Services/view.service';
import { Product } from '../../../Model/Catalog/Product';
import { Catalog } from '../../../Model/Catalog/Catalog';
import { ModelService } from '../../../Services/model.service';
import { TranslateService } from '@ngx-translate/core';
import { Message } from '../../../Model/System/Message';
import { custom } from 'devextreme/ui/dialog';
import { TemplateService } from '../../../Services/template.service';
import { UserManagementService } from '../../../Services/userManagment.service';
import { Functions } from '../../../Services/functions';
import { ProductSupplier } from '../../../Model/Catalog/ProductSupplier';
import { ValidationService } from '../../../Services/Validation/validation.service';
import { LoginService } from '../../../Services/login.service';
import { fieldNames } from '../../../Model/FieldNames';
import { ProductService } from '../../../Services/CatalogManagement/product.service';
import { CustomItemCreatingEvent } from 'devextreme/ui/select_box';

type UnitItem = { value: string; displayValue: string };

@Component({
    selector: 'productSuppliers',
    templateUrl: 'productSupplier.component.html',
    styleUrls: ['./productSupplier.component.css', '../template.css'],
    standalone: false
})
export class ProductSupplierComponent implements OnInit {
  @Input() product: Product = null;
  @Input() showLinkButtons: Boolean = false;
  @Input() catalogId: string = '';
  @Input() customerId: string = '';
  @Input() exportMode: Boolean = false;
  @Input() isTemplate: Boolean = false;
  @Output() onValueChanged = new EventEmitter<any>();
  @Output() reloadNedded = new EventEmitter<any>();

  @Input() templateView: Boolean = false;
  @Input() selectedElements: string[] = [''];
  @Output() elementSelected = new EventEmitter<string>();

  @Input() showDragHandle: boolean = false;
  @Input() dragFilter: string = 'noDrag';

  @Output() excelElementDropped = new EventEmitter<any>();
  @Output() elementDeleted = new EventEmitter<any>();

  txtExportReportProductDatasheetShort: string;
  order: number = 0;
  newOrder: number = 0;
  updateLock: boolean = false;
  unitCodeDataSource = [];
  unitDataSource: UnitItem[] = [];
  originalUnitDataSource: UnitItem[] = [];
  supplierNames = [];
  readyToEmit = false;
  priceTypes = ViewService.dropDownPriceTypes;
  supplierGroups: { key: string; name: string; items: ProductSupplier[] }[] = [];
  selectedSupplierTabIndex = 0;
  outerTabs: { text: string }[] = [];
  selectedOuterTab = 0;
  @ViewChild(PurchasingUnitsComponent) purchasingUnitsComponent: PurchasingUnitsComponent;
  newSupplierPopupVisible = false;
  newSupplierName = '';
  newSupplierId = '';

  constructor(
    public modelService: ModelService,
    public templateService: TemplateService,
    public validationService: ValidationService,
    public translate: TranslateService,
    public userManagement: UserManagementService,
    public loginService: LoginService,
    public productService: ProductService
  ) {
    this.addSupplier = this.addSupplier.bind(this);
  }

  ngOnInit(): void {
    this.txtExportReportProductDatasheetShort = this.translate.instant('ReportProductDatasheetShort');
    this.outerTabs = [
      { text: this.translate.instant('Lieferanten') },
      { text: this.translate.instant('Einkaufseinheiten') }
    ];
    this.unitCodeDataSource.push(this.translate.instant('Einheiten'));
    this.product.suppliers.forEach((x) => {
      x.guid = Functions.newGuid();
    });

    for (const productSupplier of this.product.suppliers) {
      if (productSupplier.order > this.order && productSupplier.order < 1000) {
        this.newOrder = productSupplier.order;
      }
    }
    this.newOrder += 1;
    this.generateSupplierNames();
    this.generateSupplierGroups();

    this.productService?.getOrderUnitList().subscribe((unitList) => {
      this.initializeUnits(unitList);
    });
  }

  private initializeUnits(unitList: Record<string, string>) {
    const listUnits = Object.entries(unitList).map(([id, text]) => ({ value: id, displayValue: `${this.translate.instant(text)} (${id})` }));
    this.unitDataSource = this.withStoredUnits(listUnits, (supplier) => [
      supplier.contentPackage,
      supplier.priceAllocationUnitCode,
      supplier.originalPriceAllocationUnitCode,
      supplier.contentUnitCode
    ]);
    this.originalUnitDataSource = this.withStoredUnits(listUnits, (supplier) => [supplier.originalPriceAllocationUnitCode]);
  }

  private withStoredUnits(listUnits: UnitItem[], selectStoredUnits: (supplier: ProductSupplier) => string[]): UnitItem[] {
    const listValues = new Set(listUnits.map((x) => x.value));
    const storedUnits = this.product.suppliers
      .flatMap(selectStoredUnits)
      .filter((x) => x && !listValues.has(x))
      .map((x) => ({ value: x, displayValue: x }));
    return [...new Map([...listUnits, ...storedUnits].map((x) => [x.value, x])).values()];
  }

  onUnitCustomItemCreating(event: CustomItemCreatingEvent, data: ProductSupplier, propertyName: 'contentPackage' | 'priceAllocationUnitCode' | 'contentUnitCode') {
    if (this.unitDataSource.every((x) => x.value !== event.text)) {
      this.unitDataSource = [...this.unitDataSource, { value: event.text, displayValue: event.text }];
    }
    data[propertyName] = event.text;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.readyToEmit = true;
    }, 100);
  }

  onOuterTabChange(e) {
    if (e?.itemIndex === 1) {
      this.purchasingUnitsComponent?.refresh();
    }
  }

  customizeColumns(columns) {
    columns.forEach((column) => {
      column.showEditorAlways = true;
    });
  }

  onlyUnique(value, index, array) {
    return array.indexOf(value) === index;
  }

  generateSupplierNames() {
    let names = this.product.suppliers.map((x) => x.supplierName).filter(this.onlyUnique);
    names.push(this.translate.instant('Lieferant mit niedrigster Order'));
    this.supplierNames = names;
  }

  generateSupplierGroups() {
    const groups = new Map<string, { key: string; name: string; items: ProductSupplier[] }>();
    for (const supplier of this.product.suppliers) {
      const key = supplier.supplierId?.trim() ? supplier.supplierId : '__' + supplier.guid;
      if (!groups.has(key)) {
        const name =
          supplier.supplierName?.trim() ||
          supplier.supplierId?.trim() ||
          this.translate.instant('Neuer Lieferant');
        groups.set(key, { key, name, items: [] });
      }
      groups.get(key).items.push(supplier);
    }
    this.supplierGroups = [...groups.values()];
  }

  get mainSupplierName() {
    if (this.product.mainSupplierId == null) return this.translate.instant('Lieferant mit niedrigster Order');

    let mainSupplierName = this.product.suppliers.find((x) => x.supplierId == this.product.mainSupplierId)?.supplierName;
    if (mainSupplierName != null) {
      return mainSupplierName;
    } else {
      return 'Id:' + this.product.mainSupplierId;
    }
  }

  mainSupplierChanged(e) {
    let mainSupplierId = this.product.suppliers.find((x) => x.supplierName == e.selectedItem)?.supplierId;
    if (mainSupplierId != null) {
      this.product.mainSupplierId = mainSupplierId;
    } else {
      this.product.mainSupplierId = null;
    }
    if (this.readyToEmit) {
      this.onValueChanged.emit();
    }
  }

  calculateCurrentValue(data: { validFrom: string | null; validUntil: string | null }): boolean {
    const now = Date.now();
    const isValidFrom = data.validFrom === null || Date.parse(data.validFrom) < now;
    const isValidUntil = data.validUntil === null || Date.parse(data.validUntil) > now;
    return isValidFrom && isValidUntil;
  }

  isVirtualCatalog(): boolean {
    return this.modelService?.catalogService?.catalog?.isVirtual;
  }

  update(event = null, supplierChange?, data?: ProductSupplier, field?: string, system: string = '-1', element: string = '-1') {
    //Order wird bereits benutzt
    if (field == 'SUPPLIER_ORDER') {
      let fs = this.product.suppliers.filter((x) => x.guid == data.guid)[0];
      let originalOrder = event.previousValue;
      let change = event.value > event.previousValue ? 1 : -1;
      while (this.product.suppliers.filter((x) => x.order == event.value).length > 1) {
        event.value += change;
        fs.order = event.value;
      }
      if (event.value < 1) {
        fs.order = originalOrder;
        return;
      }

      if (fs.order >= this.newOrder) {
        for (const supplier of this.product.suppliers) {
          if (supplier.order > this.newOrder && supplier.order < 1000) {
            this.newOrder = supplier.order;
          }
        }
        this.newOrder += 1;
      }
    }

    // für den template editor erforderlich:
    if (event && !event.supplierPackingChanged) {
      event.dField = field;
      event.dSystem = system;
      event.dElement = element;
      event.createTemplateItem = true;
    }

    if (field == 'SUPPLIER_HAS_BASEPRICE_DUTY' && data && !data.hasBasicPriceDuty) {
      data.contentPackage = undefined;
      data.basicQuantityNumber = undefined;
      data.contentQuantityNumber = undefined;
    }

    this.generateSupplierNames();

    this.onValueChanged.emit(event);
  }

  updateFromAutocomplete(event = null, field?: string, system: string = '-1', element: string = '-1') {
    if (event.event == undefined) {
      event.event = new Object(); // für den template editor erforderlich, da sonst keine default-items erstellt werden
    }

    // für den template editor erforderlich:
    if (event) {
      event.dField = field;
      event.dSystem = system;
      event.dElement = element;
      event.createTemplateItem = true;

      event.value = event.selectedItem; // value ist in diesem fall nicht gesetzt, darauf baut aber der template editor
    }

    this.generateSupplierNames();

    this.onValueChanged.emit(event);
  }

  delete(event = null) {
    let order = event.data.order;

    // Das Grid ist an group.items (Teilliste) gebunden; der Eintrag muss zusätzlich
    // aus der Gesamtliste product.suppliers entfernt werden.
    let removedIndex = this.product.suppliers.findIndex((x) => x.guid == event.data.guid);
    if (removedIndex >= 0) {
      this.product.suppliers.splice(removedIndex, 1);
    }

    // Prüfen, ob irgend ein Feld dieses suppliers gelinked ist
    for (let supplierField of this.templateService.allSupplierFields) {
      if (this.product != null && this.product.isFieldLinked(supplierField, order, '-1')) {
        this.product.toggleLinkedField(supplierField, order, '-1');
      }
    }

    this.onValueChanged.emit(event);

    // für den template editor erforderlich:
    event.dFields = this.templateService.allSupplierFields;
    event.dDetailFields = null;
    event.dSystem = order;
    event.dElement = -1;

    this.generateSupplierNames();
    this.generateSupplierGroups();
    if (this.selectedSupplierTabIndex >= this.supplierGroups.length) {
      this.selectedSupplierTabIndex = Math.max(0, this.supplierGroups.length - 1);
    }

    this.elementDeleted.emit(event);
  }

  openNewSupplier() {
    if (this.templateView) {
      this.addSupplier();
      return;
    }
    this.newSupplierName = '';
    this.newSupplierId = '';
    this.newSupplierPopupVisible = true;
  }

  get canConfirmNewSupplier(): boolean {
    return !!this.newSupplierName?.trim() && !!this.newSupplierId?.trim();
  }

  confirmNewSupplier() {
    if (!this.canConfirmNewSupplier) return;
    this.createSupplier(this.newSupplierName.trim(), this.newSupplierId.trim());
    this.newSupplierPopupVisible = false;
  }

  addSupplier() {
    this.createSupplier();
  }

  canAddSupplierItem = (): boolean => {
    return !this.templateView && this.renderNewSupplierButton();
  };

  addSupplierItemFromRow = (e): void => {
    const row = e?.row?.data as ProductSupplier;
    this.createSupplier(row?.supplierName, row?.supplierId);
  };

  private createSupplier(name?: string, id?: string) {
    this.updateLock = true;
    var supplier = new ProductSupplier();
    supplier.order = this.newOrder++;
    supplier.guid = Functions.newGuid();
    if (name) supplier.supplierName = name;
    if (id) supplier.supplierId = id;
    this.product.suppliers.push(supplier);
    this.update();
    this.generateSupplierGroups();
    const index = this.supplierGroups.findIndex((group) => group.items.includes(supplier));
    this.selectedSupplierTabIndex = index >= 0 ? index : this.supplierGroups.length - 1;
  }

  onContentReady() {
    this.updateLock = false;
  }

  get allowDeleting() {
    //return !this.templateView;
    return true;
  }

  translateMessage(msg: string) {
    return this.translate.instant(msg);
  }

  getEncodedString(s: string) {
    return encodeURIComponent(s);
  }

  public onRowRemoving(e) {
    let order = e.data.supplierOrder;

    if (this.product != null && this.product.isChild && this.hasSupplierFieldLinked(order)) {
    // Löschen nicht erlaubt, INFO

      let txtNotAllowed = '';
      this.translate.get('DeleteLinkedFieldNotAllowed').subscribe((text: string) => {
        txtNotAllowed = text;
      });

      this.modelService.systemService.notify(new Message(txtNotAllowed, 'info'), 3000);
      e.cancel = true;
      return;
    }

    // Löschen erlaubt, Benutzerabfrage, ggf. Abbruch
    let txtYes = '';
    let txtNo = '';
    let txtMsg = '';
    let txtTitle = '';
    let myDialog: any;

    this.translate.get('Ja').subscribe((text: string) => {
      txtYes = text;
    });
    this.translate.get('Nein').subscribe((text: string) => {
      txtNo = text;
    });
    this.translate.get('Wollen Sie den Lieferanten wirklich löschen').subscribe((text: string) => {
      txtMsg = text;
      if (!txtMsg.endsWith('?')) {
        // Übersetzungen sid nicht einheitlich gepflegt, werden aber auch an anderen Stellen so verwendet.
        txtMsg = txtMsg + '?';
      }
    });
    this.translate.get('Wirklich löschen?').subscribe((text: string) => {
      txtTitle = text;
    });

    myDialog = custom({
      title: txtTitle,
      messageHtml: txtMsg,
      buttons: [
        {
          text: txtYes,
          onClick: (e) => {
            return false;
          }
        },
        {
          text: txtNo,
          onClick: (e) => {
            return true;
          }
        }
      ]
    });

    // Hier wird ein Promise zurück geliefert. Dies enthält den Return Value des (erst später!!)
    // ausgeführten onClick-Handlers des gedrückten Buttons im Dialog.
    // https://supportcenter.devexpress.com/ticket/details/t978828/datagrid-how-to-cancel-the-rowremoving-event
    e.cancel = myDialog.show();
  }

  //ExcelTemplate

  templateSelect(identifier: string, order: string) {
    this.elementSelected.emit(`${identifier}_${order}`);
  }

  excelElementDrop(e) {
    this.excelElementDropped.emit(e);
  }

  public hasSupplierFieldLinked(system: string = '-1'): boolean {
    // Prüfen, ob irgend ein Feld dieses suppliers gelinked ist
    for (let supplierField of this.templateService.allSupplierFields) {
      if (this.product != null && this.product.isFieldLinked(supplierField, system, '-1')) {
        return true;
      }
    }
    return false;
  }

  public readOnlyForLinkedFields(field: string, system: string = '-1', element: string = '-1'): boolean {
    // Wenn dies ein vom Master vererbtes Feld ist, soll es ReadOnly sein:
    if (this.product != null && field != null) {
      var isLinked = this.product.isFieldLinked(field, system, element);
      if (isLinked && this.product.isChild != null && this.product.isChild) {
        // Für einen Master darf es nicht ReadOnly werden!!
        return true;
      }
    }

    //return this.readOnly(productId);

    return false;
  }

  public showClearButton(field: string, system: string): boolean {
    return !this.readOnlyForLinkedFields(field, system) && !this.templateView;
  }

  public hasMasterOrChildOrIsTranslated(): boolean {
    if (this.product != null) {
      return this.product.hasMasterOrChild();
    }
    return false;
  }

  public isFieldLinked(field: string, system: string = '-1', element: string = '-1'): boolean {
    if (this.product != null) {
      return this.product.isFieldLinked(field, system, element);
    }
    return false;
  }

  public toggleLinkedField(event = null, field: string, system: string = '-1', element: string = '-1') {
    var changed = this.product.toggleLinkedField(field, system, element);
    if (!changed) {
      return;
    }

    var isNowLinked = this.isFieldLinked(field, system, element);
    event.target.className = this.product.getClassForLinkedField(isNowLinked);

    if (this.product.isChild && isNowLinked) {
      event.forceReload = true;
    }

    if (field == 'supplier_PURPOSE' || field == 'supplier_SOURCE') {
      // Etwas schmutzig, aber sonst können wir den Refresh am Tree nicht antriggern.
      event.forceTreeRefresh = true;
    }

    // SAVE
    this.update(event, true);
  }

  public getInitialClassForLinkedField(field: string, system: string = '-1', element: string = '-1'): string {
    var isNowLinked = this.isFieldLinked(field, system, element);
    return this.product.getClassForLinkedField(isNowLinked);
  }

  get internationalPidType() {
    return ViewService.dropDownInternationalPidType;
  }

  canView(name: string): boolean {
    // Wenn product == null befinden wir uns im Katalog oder einer Kategorie.
    return (this.product == null || !this.isVirtualCatalog()) && this.userManagement.canView(name);
  }
  readOnly(name: string, catalog: Catalog = this.modelService.catalogService.catalog): boolean {
    // Wenn product == null befinden wir uns im Katalog oder einer Kategorie.
    return (this.product != null && this.isVirtualCatalog()) || !this.userManagement.canEdit(name, catalog);
  }

  renderNewSupplierButton(): boolean {
    // Wenn product == null befinden wir uns im Katalog oder einer Kategorie.
    return (this.product == null || !this.isVirtualCatalog()) && this.canView('supplierNew');
  }
}
